import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import translations from '../translations';

const LanguageContext = createContext();

const STORAGE_KEY = 'universe-website-language';
const DEFAULT_LANGUAGE = 'de';

/**
 * Language Provider component
 * Manages language state for the website with German as default
 * Persists language choice in localStorage
 */
export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage for saved preference
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && (saved === 'de' || saved === 'en')) {
        return saved;
      }
    }
    return DEFAULT_LANGUAGE;
  });

  // Update localStorage when language changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, language);
      // Update html lang attribute for accessibility
      document.documentElement.lang = language;
    }
  }, [language]);

  // Toggle between German and English
  const toggleLanguage = useCallback(() => {
    setLanguageState(prev => prev === 'de' ? 'en' : 'de');
  }, []);

  // Set language directly
  const setLanguage = useCallback((lang) => {
    if (lang === 'de' || lang === 'en') {
      setLanguageState(lang);
    }
  }, []);

  /**
   * Get translation for a key path
   * @param {string} key - Dot-notation path (e.g., 'hero.title')
   * @param {string} fallback - Fallback value if key not found
   * @returns {string} - Translated string
   */
  const t = useCallback((key, fallback = '') => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Try fallback to German
        let fallbackValue = translations[DEFAULT_LANGUAGE];
        for (const fk of keys) {
          if (fallbackValue && typeof fallbackValue === 'object' && fk in fallbackValue) {
            fallbackValue = fallbackValue[fk];
          } else {
            return fallback || key;
          }
        }
        return typeof fallbackValue === 'string' ? fallbackValue : fallback || key;
      }
    }

    return typeof value === 'string' ? value : fallback || key;
  }, [language]);

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t,
    isGerman: language === 'de',
    isEnglish: language === 'en'
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Hook to access language context
 * @returns {Object} - Language context value
 */
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
